import {
  ArrowRight,
  Banknote,
  CreditCard,
  Delete,
  IndianRupee,
  Plus,
  Printer,
  Smartphone,
  Split,
  Trash2,
  WalletCards,
  X,
} from "lucide-react";
import { isPosCheckoutPaymentMethod } from "@medicorepay/shared";
import { useState } from "react";

interface Totals {
  grandTotal: number;
}

export interface SplitPaymentLine {
  method: string;
  amount: number;
  reference?: string;
}

interface PaymentModalProps {
  totals: Totals;
  paymentMethod: string;
  setPaymentMethod: (method: string) => void;
  amountTendered: string;
  setAmountTendered: (amount: string) => void;
  changeAmount: number;
  processing: boolean;
  error: string;
  allowedMethods?: readonly string[];
  onPayment: (shouldPrint: boolean, splitPayments?: SplitPaymentLine[]) => void;
  onClose: () => void;
}

const PAYMENT_METHODS = [
  { id: "CASH", label: "Cash", icon: Banknote, color: "emerald" },
  { id: "UPI", label: "UPI", icon: Smartphone, color: "violet" },
  { id: "CARD", label: "Card", icon: CreditCard, color: "blue" },
  { id: "LOYALTY", label: "Loyalty", icon: WalletCards, color: "amber" },
  { id: "ADVANCE", label: "Advance", icon: WalletCards, color: "amber" },
  { id: "BANK_TRANSFER", label: "Bank", icon: IndianRupee, color: "blue" },
  { id: "CHEQUE", label: "Cheque", icon: IndianRupee, color: "blue" },
  { id: "STORE_WALLET", label: "Wallet", icon: WalletCards, color: "amber" },
] as const;

const QUICK_AMOUNTS = [50, 100, 200, 500, 1000, 2000];

const NUMPAD_KEYS = [
  "1",
  "2",
  "3",
  "4",
  "5",
  "6",
  "7",
  "8",
  "9",
  ".",
  "0",
  "⌫",
] as const;

const REFERENCE_REQUIRED_METHODS = new Set(["BANK_TRANSFER", "CHEQUE"]);

const REFERENCE_VISIBLE_METHODS = new Set([
  "UPI",
  "CARD",
  ...REFERENCE_REQUIRED_METHODS,
]);

function paymentReferencePlaceholder(method: string): string {
  if (method === "UPI") return "UPI transaction / UTR reference";
  if (method === "CARD") return "Card terminal receipt/reference";
  return "Bank / cheque reference";
}

export function PaymentModal({
  totals,
  paymentMethod,
  setPaymentMethod,
  amountTendered,
  setAmountTendered,
  changeAmount,
  processing,
  error,
  allowedMethods = ["CASH", "UPI", "CARD"],
  onPayment,
  onClose,
}: PaymentModalProps) {
  const [useNumpad, setUseNumpad] = useState(false);
  const [splitMode, setSplitMode] = useState(false);
  const [splitLines, setSplitLines] = useState<SplitPaymentLine[]>([
    { method: "CASH", amount: 0 },
  ]);
  const [reference, setReference] = useState("");
  const availablePaymentMethods = PAYMENT_METHODS.filter(
    (method) =>
      isPosCheckoutPaymentMethod(method.id) &&
      allowedMethods.includes(method.id),
  );

  const handleNumpadPress = (key: string) => {
    if (key === "⌫") {
      setAmountTendered(amountTendered.slice(0, -1));
    } else if (key === ".") {
      if (!amountTendered.includes(".")) {
        setAmountTendered(amountTendered + ".");
      }
    } else {
      setAmountTendered(amountTendered + key);
    }
  };

  const handleExactAmount = () => {
    setAmountTendered(totals.grandTotal.toFixed(2));
  };

  // Split payment helpers
  const splitTotal = splitLines.reduce((s, l) => s + (l.amount || 0), 0);
  const splitRemaining = totals.grandTotal - splitTotal;

  const addSplitLine = () => {
    const usedMethods = splitLines.map((l) => l.method);
    const nextMethod =
      availablePaymentMethods.find((m) => !usedMethods.includes(m.id))?.id ||
      availablePaymentMethods[0]?.id ||
      "CASH";
    setSplitLines([
      ...splitLines,
      {
        method: nextMethod,
        amount: Math.max(0, Number(splitRemaining.toFixed(2))),
      },
    ]);
  };

  const updateSplitLine = (
    index: number,
    field: "method" | "amount" | "reference",
    value: string | number,
  ) => {
    const updated = [...splitLines];
    if (field === "method") {
      updated[index] = { ...updated[index], method: value as string };
    } else if (field === "reference") {
      updated[index] = { ...updated[index], reference: value as string };
    } else {
      updated[index] = { ...updated[index], amount: Number(value) || 0 };
    }
    setSplitLines(updated);
  };

  const removeSplitLine = (index: number) => {
    if (splitLines.length <= 1) return;
    setSplitLines(splitLines.filter((_, i) => i !== index));
  };

  const toggleSplitMode = () => {
    if (!splitMode) {
      // Entering split mode: pre-fill first line
      setSplitLines([
        { method: paymentMethod || "CASH", amount: totals.grandTotal },
      ]);
    }
    setSplitMode(!splitMode);
  };

  const handleSubmit = (shouldPrint: boolean) => {
    if (splitMode) {
      onPayment(
        shouldPrint,
        splitLines.filter((l) => l.amount > 0),
      );
    } else {
      onPayment(shouldPrint, [
        {
          method: paymentMethod,
          amount: Number(amountTendered),
          ...(reference.trim() ? { reference: reference.trim() } : {}),
        },
      ]);
    }
  };

  // Determine if payment is valid
  const referenceRequired = REFERENCE_REQUIRED_METHODS.has(paymentMethod);
  const referenceVisible = REFERENCE_VISIBLE_METHODS.has(paymentMethod);
  const splitReferencesValid = splitLines.every(
    (line) =>
      !REFERENCE_REQUIRED_METHODS.has(line.method) ||
      Boolean(line.reference?.trim()),
  );
  const canPay = splitMode
    ? Math.abs(splitTotal - totals.grandTotal) < 0.005 &&
      splitLines.some((l) => l.amount > 0) &&
      splitReferencesValid
    : !!amountTendered &&
      (paymentMethod === "CASH"
        ? changeAmount >= 0
        : Math.abs(changeAmount) < 0.005) &&
      (!referenceRequired || Boolean(reference.trim()));

  const methodColors: Record<string, string> = {
    emerald:
      "border-emerald-500 bg-emerald-50 text-emerald-700 shadow-emerald-100",
    violet: "border-violet-500 bg-violet-50 text-violet-700 shadow-violet-100",
    blue: "border-blue-500 bg-blue-50 text-blue-700 shadow-blue-100",
    amber: "border-amber-500 bg-amber-50 text-amber-700 shadow-amber-100",
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center overflow-y-auto bg-black/70 p-4 backdrop-blur-sm">
      <div className="flex max-h-[calc(100vh-2rem)] w-full max-w-lg flex-col overflow-hidden rounded-2xl bg-white shadow-2xl animate-scale-in">
        {/* Header */}
        <div className="bg-gradient-to-r from-gray-900 to-gray-800 px-6 py-4 flex justify-between items-center">
          <div>
            <h2 className="text-lg font-bold text-white">Complete Payment</h2>
            <p className="text-xs text-gray-400 mt-0.5">
              Review and finalize the transaction
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white p-1 hover:bg-white/10 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="min-h-0 flex-1 space-y-5 overflow-y-auto p-6">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm font-medium">
              {error}
            </div>
          )}

          {/* Total Amount Display */}
          <div className="text-center py-2">
            <div className="text-[10px] text-gray-400 uppercase tracking-widest font-bold mb-1">
              Amount Due
            </div>
            <div className="text-4xl font-extrabold text-gray-900 tracking-tight tabular-nums">
              ₹{totals.grandTotal.toFixed(2)}
            </div>
          </div>

          {/* Payment Methods */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider">
                Payment Method
              </label>
              <button
                onClick={toggleSplitMode}
                className={`flex items-center gap-1 text-[10px] font-bold px-2 py-1 rounded-lg transition-colors ${
                  splitMode
                    ? "bg-violet-100 text-violet-700"
                    : "text-gray-400 hover:text-gray-600 hover:bg-gray-100"
                }`}
              >
                <Split className="w-3 h-3" />
                {splitMode ? "Single Payment" : "Split Payment"}
              </button>
            </div>

            {!splitMode && (
              <div className="grid grid-cols-3 gap-2">
                {availablePaymentMethods.map((method) => {
                  const Icon = method.icon;
                  const isActive = paymentMethod === method.id;
                  return (
                    <button
                      key={method.id}
                      onClick={() => setPaymentMethod(method.id)}
                      className={`flex flex-col items-center gap-1.5 py-3 px-2 rounded-xl border-2 transition-all ${
                        isActive
                          ? `${methodColors[method.color]} shadow-md`
                          : "border-gray-100 hover:border-gray-200 text-gray-500 hover:text-gray-700"
                      }`}
                    >
                      <Icon
                        className={`w-5 h-5 ${isActive ? "" : "text-gray-400"}`}
                      />
                      <span className="text-xs font-bold">{method.label}</span>
                    </button>
                  );
                })}
              </div>
            )}

            {splitMode && (
              <div className="space-y-2">
                {splitLines.map((line, idx) => (
                  <div
                    key={idx}
                    className="flex items-center gap-2 bg-gray-50 rounded-xl p-2 border border-gray-100"
                  >
                    <select
                      value={line.method}
                      onChange={(e) =>
                        updateSplitLine(idx, "method", e.target.value)
                      }
                      className="h-9 px-2 border border-gray-200 rounded-lg text-xs font-bold focus:ring-1 focus:ring-teal-500 outline-none bg-white text-gray-700 w-24"
                    >
                      {availablePaymentMethods.map((m) => (
                        <option key={m.id} value={m.id}>
                          {m.label}
                        </option>
                      ))}
                    </select>
                    <div className="relative flex-1">
                      <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-xs">
                        ₹
                      </span>
                      <input
                        type="number"
                        value={line.amount || ""}
                        onChange={(e) =>
                          updateSplitLine(
                            idx,
                            "amount",
                            parseFloat(e.target.value) || 0,
                          )
                        }
                        placeholder="0.00"
                        className="w-full pl-6 pr-2 h-9 border border-gray-200 rounded-lg text-right text-sm font-bold text-gray-900 focus:ring-1 focus:ring-teal-500 outline-none tabular-nums"
                      />
                    </div>
                    {REFERENCE_VISIBLE_METHODS.has(line.method) && (
                      <input
                        type="text"
                        value={line.reference ?? ""}
                        onChange={(e) =>
                          updateSplitLine(idx, "reference", e.target.value)
                        }
                        placeholder={paymentReferencePlaceholder(line.method)}
                        aria-label={`${line.method} payment reference`}
                        className="w-28 h-9 px-2 border border-gray-200 rounded-lg text-xs"
                      />
                    )}
                    {splitLines.length > 1 && (
                      <button
                        onClick={() => removeSplitLine(idx)}
                        className="p-1.5 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                ))}
                {splitLines.length < availablePaymentMethods.length && (
                  <button
                    onClick={addSplitLine}
                    className="flex items-center gap-1.5 text-xs font-bold text-teal-600 hover:text-teal-700 px-3 py-1.5 hover:bg-teal-50 rounded-lg transition-colors"
                  >
                    <Plus className="w-3 h-3" />
                    Add payment method
                  </button>
                )}
                <div className="flex justify-between items-center text-xs pt-1 border-t border-gray-200 mt-2">
                  <span className="text-gray-500 font-medium">
                    Total: ₹{splitTotal.toFixed(2)}
                  </span>
                  {splitRemaining > 0.01 ? (
                    <span className="text-amber-600 font-bold">
                      Remaining: ₹{splitRemaining.toFixed(2)}
                    </span>
                  ) : splitRemaining < -0.01 ? (
                    <span className="text-emerald-600 font-bold">
                      Over allocated: ₹{Math.abs(splitRemaining).toFixed(2)}
                    </span>
                  ) : (
                    <span className="text-emerald-600 font-bold">
                      ✓ Fully covered
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>

          {!splitMode && referenceVisible && (
            <div className="space-y-1.5">
              <input
                type="text"
                value={reference}
                onChange={(event) => setReference(event.target.value)}
                placeholder={paymentReferencePlaceholder(paymentMethod)}
                aria-label={`${paymentMethod} payment reference`}
                className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm"
              />
              {["UPI", "CARD"].includes(paymentMethod) && (
                <p className="text-[10px] text-gray-500">
                  Optional: enter the UTR or terminal receipt reference for
                  easier reconciliation.
                </p>
              )}
            </div>
          )}

          {/* Amount Input + Quick Buttons (single payment only) */}
          {!splitMode && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">
                  Amount Tendered
                </label>
                <button
                  onClick={() => setUseNumpad((v) => !v)}
                  className="text-[10px] font-semibold text-teal-600 hover:text-teal-700"
                >
                  {useNumpad ? "Use keyboard" : "Show numpad"}
                </button>
              </div>

              <div className="relative mb-3">
                <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400">
                  <IndianRupee className="w-4 h-4" />
                </span>
                <input
                  type="number"
                  value={amountTendered}
                  onChange={(e) => setAmountTendered(e.target.value)}
                  placeholder="0.00"
                  className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-teal-500 focus:ring-0 text-xl font-bold text-gray-900 tabular-nums"
                  autoFocus={!useNumpad}
                  readOnly={useNumpad}
                />
              </div>

              {/* Quick Amount Buttons */}
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={handleExactAmount}
                  className="px-3 py-1.5 bg-teal-50 border border-teal-200 rounded-lg text-xs font-bold text-teal-700 hover:bg-teal-100 transition-colors"
                >
                  Exact
                </button>
                {QUICK_AMOUNTS.map((amount) => (
                  <button
                    key={amount}
                    onClick={() =>
                      setAmountTendered(
                        ((parseFloat(amountTendered) || 0) + amount).toString(),
                      )
                    }
                    className="px-3 py-1.5 bg-gray-50 border border-gray-200 rounded-lg text-xs font-bold text-gray-600 hover:bg-gray-100 hover:border-gray-300 transition-colors tabular-nums"
                  >
                    +₹{amount}
                  </button>
                ))}
              </div>

              {/* Numpad */}
              {useNumpad && (
                <div className="grid grid-cols-3 gap-2 mt-3">
                  {NUMPAD_KEYS.map((key) => (
                    <button
                      key={key}
                      onClick={() => handleNumpadPress(key)}
                      className="h-12 bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-xl text-lg font-bold text-gray-700 transition-colors flex items-center justify-center active:scale-95"
                    >
                      {key === "⌫" ? <Delete className="w-5 h-5" /> : key}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Change / Balance (single payment only) */}
          {!splitMode && paymentMethod === "CASH" && changeAmount > 0 && (
            <div className="bg-emerald-50 border border-emerald-200 p-4 rounded-xl flex justify-between items-center">
              <div className="flex items-center gap-2">
                <Banknote className="w-5 h-5 text-emerald-600" />
                <span className="text-sm font-bold text-emerald-700">
                  Change to Return
                </span>
              </div>
              <span className="text-2xl font-extrabold text-emerald-800 tabular-nums">
                ₹{changeAmount.toFixed(2)}
              </span>
            </div>
          )}

          {!splitMode && changeAmount < 0 && amountTendered && (
            <div className="bg-amber-50 border border-amber-200 p-4 rounded-xl flex justify-between items-center">
              <div className="flex items-center gap-2">
                <IndianRupee className="w-5 h-5 text-amber-600" />
                <span className="text-sm font-bold text-amber-700">
                  Balance Due
                </span>
              </div>
              <span className="text-2xl font-extrabold text-amber-800 tabular-nums">
                ₹{Math.abs(changeAmount).toFixed(2)}
              </span>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex shrink-0 gap-3 border-t border-gray-100 bg-gray-50 px-6 py-4">
          <button
            onClick={() => handleSubmit(false)}
            disabled={processing || !canPay}
            className="flex-1 py-3 bg-gradient-to-r from-gray-800 to-gray-900 text-white rounded-xl font-bold text-sm hover:from-gray-700 hover:to-gray-800 disabled:from-gray-300 disabled:to-gray-300 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2 shadow-lg shadow-gray-200"
          >
            {processing ? (
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Processing...
              </div>
            ) : (
              <>
                Complete
                <ArrowRight className="w-4 h-4" />
              </>
            )}
          </button>
          <button
            onClick={() => handleSubmit(true)}
            disabled={processing || !canPay}
            className="flex-1 py-3 bg-gradient-to-r from-teal-500 to-emerald-500 text-white rounded-xl font-bold text-sm hover:from-teal-600 hover:to-emerald-600 disabled:from-gray-300 disabled:to-gray-300 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2 shadow-lg shadow-teal-200"
          >
            {processing ? (
              "..."
            ) : (
              <>
                <Printer className="w-4 h-4" />
                Print & Complete
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
